import gemojis from 'gemoji'
import {
  CHAT_MENTION as MENTION_RE,
  CHAT_EMOTE as EMOTE_RE,
  CHAT_EMOJI_UNICODES as UNICODE_RE,
  URL_RE,
  CHAT_AC_EMOTE,
  CHAT_AC_MENTION,
} from '@/utils/constants/regexes'
import { AC_TYPE_EMOTE, AC_TYPE_MENTION, EMOJI_BASE_URL } from '@/utils/constants/chat'
import customEmotes from '@/utils/constants/custom-emotes'

export const BLOCK_REGEX = new RegExp(`(${MENTION_RE})|(${EMOTE_RE}|${UNICODE_RE})|(${URL_RE})`, 'g')

/**
 * Converts unicode to codepoint equivalent
 * @param {String} unicode
 * @returns {String} codepoint
 */
export function toCodePoint(unicode) {
  const r = []
  let c = 0
  let p = 0
  let i = 0

  /* eslint-disable */
  while (i < unicode.length) {
    c = unicode.charCodeAt(i++)
    if (p) {
      r.push(((p << 10) + c - 56613888).toString(16))
      p = 0
    } else if (c >= 0xd800 && c <= 0xdbff) {
      p = c
    } else {
      r.push(c.toString(16))
    }
  }
  /* eslint-enable */
  return r.join('-')
}

/**
 * Searches for emoji in gemoji and returns gemoji object
 * @param {String} emote
 * @returns {Object} gemoji object
 */
export const toGemoji = emote => {
  for (const gemoji of gemojis) {
    if (gemoji.emoji === emote) return gemoji
    if (gemoji.names.includes(emote)) return gemoji
  }

  return null
}

/**
 * @param {String} emote
 * @returns {String} url of emoji
 */
export const toEmoteUrl = emote => {
  const customEmote = customEmotes[emote]
  // eslint-disable-next-line global-require
  if (customEmote) return require(`@/assets/img/emotes/${customEmote}`)

  const gemoji = toGemoji(emote)
  if (gemoji) return `${EMOJI_BASE_URL}/${toCodePoint(gemoji.emoji)}.png`

  return null
}

/**
 * Extracts chat message component blocks out of a message string
 * @param {String} message input message
 * @returns {Object[]} array of component blocks
 */
export const tokenize = message => {
  const tokens = []
  const blocks = []

  const matches = message.matchAll(BLOCK_REGEX)

  for (const match of matches) {
    if (match[1]) {
      const input = match[1]
      blocks.push({
        type: 'mention',
        payload: input.substring(1),
        from: match.index,
        to: match.index + input.length,
      })
    }

    if (match[2]) {
      const input = match[2]

      const emote = input.replace(/:/g, '')
      const emoteUrl = toEmoteUrl(emote)

      if (emoteUrl) {
        blocks.push({
          type: 'emote',
          payload: {
            name: emote,
            input,
            url: emoteUrl,
          },
          from: match.index,
          to: match.index + input.length,
        })
      }
    }

    if (match[3]) {
      const input = match[3]

      let url = input

      if (!(/^(http|https)/.test(url))) {
        url = `http://${input}`
      }

      blocks.push({
        type: 'url',
        payload: { input, url },
        from: match.index,
        to: match.index + input.length,
      })
    }
  }

  if (blocks.length) {
    let currIndex = 0
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i]

      // don't push to tokens if empty string
      if (message.substring(currIndex, block.from) === '') {
        currIndex = block.to

        // push block
        tokens.push({
          type: block.type,
          payload: block.payload,
        })
      } else {
        tokens.push({
          type: 'rawtext',
          payload: message.substring(currIndex, block.from),
        })
        currIndex = block.to

        // push block behind text
        tokens.push({
          type: block.type,
          payload: block.payload,
        })
      }
    }

    // Last element
    if (message.substring(currIndex, message.length) !== '') {
      tokens.push({
        type: 'rawtext',
        payload: message.substring(currIndex, message.length),
      })
    }
  } else {
    tokens[0] = {
      type: 'rawtext',
      payload: message,
    }
  }

  return tokens
}

/**
 * Extracts and dedupes all mentions from a message
 * @param {String} message
 * @returns {Array} array of mentions
 */
export const toMentionsArray = message => [...new Set(
  [...message.matchAll(new RegExp(MENTION_RE, 'g'))].map(i => i[0].substring(1)),
)]

export const emoteList = [
  ...Object.keys(customEmotes),
  ...gemojis.flatMap(i => i.names),
]

export const getAutocompleteType = input => {
  const emote = new RegExp(CHAT_AC_EMOTE).test(input)
  if (emote) return AC_TYPE_EMOTE

  const mention = new RegExp(CHAT_AC_MENTION).test(input)
  if (mention) return AC_TYPE_MENTION

  // TODO add commands?

  return null
}

/**
 * Finds mention in string and returns it if found
 * @param {String} input input string
 * @returns string match or null
 */
export const toMention = input => input.match(new RegExp(CHAT_AC_MENTION))?.[0]

/**
 * Finds emote in string and returns it if found
 * @param {String} input input string
 * @returns string match or null
 */
export const toEmote = input => input.match(new RegExp(CHAT_AC_EMOTE))?.[0]

/**
 * Replaces an autocomplete emote with the full one
 * @param {String} input input string
 * @returns {String} output string
 */
export const replaceEmote = (input, emote) => input.replace(new RegExp(CHAT_AC_EMOTE), `:${emote}: `)

/**
 * Replaces an autocomplete mention with the full one
 * @param {String} input input string
 * @returns {String} output string
 */
export const replaceMention = (input, mention) => input.replace(new RegExp(CHAT_AC_MENTION), `@${mention} `)

export const replaceWhitespace = text => text.replace(/\s+/g, ' ')
